import type { VesselHistorySelection } from "../models/vessel-data.models";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { type MouseEvent, useEffect, useRef } from "react";

const label = (key: string) => key.replace(/([A-Z])/g, " $1").replace(/^./, (character) => character.toUpperCase());

// Reveals the complete source row while compact tables retain the Power BI overview density.
const VesselHistoryModal = ({ selection, onClose }: { selection: VesselHistorySelection; onClose: () => void }) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousBodyOverflow = document.body.style.overflow;

    const keepFocusInsideModal = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();

        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>("button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])") ?? [],
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) {
        event.preventDefault();

        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", keepFocusInsideModal);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.removeEventListener("keydown", keepFocusInsideModal);
      previouslyFocused?.focus();
    };
  }, [onClose]);

  const closeFromBackdrop = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vessel-data-history-modal-title"
      data-test="vessel-data-history-modal"
      onMouseDown={closeFromBackdrop}
    >
      <div ref={dialogRef} className="max-h-[85vh] w-full max-w-4xl overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b px-5 py-4 dark:border-gray-700">
          <h2 id="vessel-data-history-modal-title" className="font-semibold">{selection.title}</h2>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close row details" data-test="vessel-data-history-modal-close">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <dl className="grid max-h-[70vh] grid-cols-1 gap-3 overflow-y-auto p-5 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(selection.values).map(([key, entry]) => (
            <div key={key} className="rounded-lg bg-gray-50 p-3 dark:bg-gray-900/50">
              <dt className="text-[10px] font-semibold uppercase text-gray-500">{label(key)}</dt>
              <dd className="mt-1 break-words text-sm">
                {selection.kind === "noon" && key === "formLink" && typeof entry === "string" && entry ? (
                  <a href={entry} target="_blank" rel="noreferrer" className="font-semibold text-green underline" data-test="vessel-data-noon-form-link">Open noon form</a>
                ) : entry === null || entry === "" ? "—" : typeof entry === "boolean" ? (entry ? "True" : "False") : String(entry)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
};

export default VesselHistoryModal;
